import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthProvider as AuthProviderType } from 'src/auth/types';
import { useAuthenticationFlow } from 'src/hooks/useAuthenticationFlow';
import { cn } from 'src/utils/cn';
import { EnhancedLoadingState } from './EnhancedLoadingStates';
import { AuthLoadingStates } from './authLoadingStates';
import { AuthLayout } from 'src/layout/AuthLayout';
import { AuthLogo } from './AuthLogo';
import { UI_TRANSITION_DELAY } from 'src/auth/constants';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { ArrowRightIcon, MailIcon } from 'lucide-react';
import { toast } from 'react-toastify';

interface CustomLoginPageProps {
  readonly authProvider: AuthProviderType;
  readonly onLogin: () => Promise<void>;
  readonly isLoading?: boolean;
  readonly error?: string;
  readonly onMagicLinkLogin: (email: string) => Promise<boolean>;
}

/**
 * Detect if user is on iOS
 */
const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Microsoft Icon Component
 */
const MicrosoftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 23 23" fill="currentColor">
    <path d="M1 1h10v10H1z" fill="#f25022" />
    <path d="M12 1h10v10H12z" fill="#00a4ef" />
    <path d="M1 12h10v10H1z" fill="#ffb900" />
    <path d="M12 12h10v10H12z" fill="#7fba00" />
  </svg>
);

/**
 * Keycloak Icon Component
 */
const KeycloakIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z" />
  </svg>
);

/**
 * Get provider-specific configuration
 */
const getProviderConfig = (provider: AuthProviderType) => {
  if (provider === 'entra') {
    return {
      displayName: 'Microsoft',
      buttonText: 'Sign in with Microsoft',
      description: 'Mortgage Operations Services',
      icon: MicrosoftIcon,
      colors:
        'bg-True-Blue text-white hover:bg-Blue-Dark-Text focus:ring-True-Blue shadow-sm hover:shadow-md',
      footer: 'TRUE Cloud Platform',
    };
  }

  return {
    displayName: 'Keycloak',
    buttonText: 'Continue with Keycloak',
    description: 'Sign in with your organization account',
    icon: KeycloakIcon,
    colors:
      'bg-Green-Text text-white hover:bg-Green-Dark focus:ring-Green-Text shadow-sm hover:shadow-md',
    footer: 'Keycloak',
  };
};

export const CustomLoginPage: React.FC<CustomLoginPageProps> = ({
  authProvider,
  onLogin,
  onMagicLinkLogin,
  isLoading = false,
  error,
}) => {
  const runtimeConfig = getRuntimeConfig();
  const enableMagicLink =
    runtimeConfig.VITE_SUPPORT_MAGIC_LINK === 'true' ||
    runtimeConfig.VITE_SUPPORT_MAGIC_LINK === true;
  const navigate = useNavigate();
  const [showMagicLinkInput, setShowMagicLinkInput] = useState<boolean>(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState<string>('');
  const [flowState, flowActions] = useAuthenticationFlow();
  const loginButtonRef = useRef<HTMLButtonElement>(null);
  const isIOS = isIOSDevice();

  // Extract configuration and computed values
  const providerConfig = getProviderConfig(authProvider);
  const isAuthenticating =
    flowState.state === 'initiating' ||
    flowState.state === 'redirecting' ||
    isLoading;
  const displayError = error ?? flowState.error;
  const validateEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') {
      return false;
    }

    // Check for whitespace
    if (email.includes(' ')) {
      return false;
    }

    // Check for @ symbol
    const atIndex = email.indexOf('@');
    if (atIndex === -1 || atIndex === 0) {
      return false; // No @ or @ at start
    }

    // Check local part (before @)
    const localPart = email.substring(0, atIndex);
    if (localPart.length === 0) {
      return false;
    }

    // Check domain part (after @)
    const domainPart = email.substring(atIndex + 1);
    if (domainPart.length === 0) {
      return false;
    }

    // Check for dot in domain
    const dotIndex = domainPart.indexOf('.');
    if (dotIndex === -1 || dotIndex === 0) {
      return false; // No dot or dot at start of domain
    }

    // Check TLD (after last dot)
    const lastDotIndex = domainPart.lastIndexOf('.');
    const tld = domainPart.substring(lastDotIndex + 1);
    if (tld.length === 0) {
      return false;
    }

    return true;
  };
  const handleMagicLinkLogin = async () => {
    if (!validateEmail(magicLinkEmail)) {
      toast.error('Please enter a valid email');
      return;
    }
    await onMagicLinkLogin(magicLinkEmail).then((success) => {
      if (success) {
        navigate(
          `/magic-link/enter-code?email=${encodeURIComponent(magicLinkEmail)}`,
        );
      }
    });
  };

  // Focus management for accessibility
  useEffect(() => {
    if (!isAuthenticating && !displayError && loginButtonRef.current) {
      loginButtonRef.current.focus();
    }
  }, [isAuthenticating, displayError]);

  // Prevent zoom on iOS when focusing inputs
  useEffect(() => {
    if (isIOS) {
      const viewport = document.querySelector('meta[name=viewport]');
      if (viewport) {
        viewport.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
        );
      }
    }

    return () => {
      if (isIOS) {
        const viewport = document.querySelector('meta[name=viewport]');
        if (viewport) {
          viewport.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0',
          );
        }
      }
    };
  }, [isIOS]);

  const handleLogin = async () => {
    try {
      flowActions.startAuth();

      // Brief delay for smooth UI transition
      await new Promise((resolve) => setTimeout(resolve, UI_TRANSITION_DELAY));

      flowActions.setRedirecting();

      // Initiate authentication
      await onLogin();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Authentication failed';
      flowActions.setError(errorMessage);
    }
  };

  return (
    <AuthLayout>
      <div className="text-center space-y-6">
        {/* Logo Section */}
        <div className="space-y-6">
          <AuthLogo size="md" />
          <div className="space-y-6">
            <h1 id="login-title" className="text-4xl font-bold text-gray-900">
              M O S
            </h1>
            <p className="text-gray-600" id="auth-description">
              {providerConfig.description}
            </p>
          </div>
        </div>

        {/* Authentication Section */}
        <div className="space-y-4">
          {!isAuthenticating && !displayError && (
            <button
              ref={loginButtonRef}
              onClick={handleLogin}
              className={cn(
                'w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-full font-medium',
                'transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'touch-manipulation active:scale-[0.98]',
                'max-sm:py-4 max-sm:text-base',
                providerConfig.colors,
              )}
              disabled={isLoading}
              aria-label={`Sign in with ${providerConfig.displayName}`}
              aria-describedby="auth-description"
            >
              <providerConfig.icon className="w-5 h-5" aria-hidden="true" />
              {providerConfig.buttonText}
            </button>
          )}
          {!isAuthenticating && enableMagicLink && !showMagicLinkInput && (
            <button
              onClick={() => setShowMagicLinkInput(true)}
              type="button"
              className={cn(
                'w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-full font-medium',
                'border border-gray-200 bg-black text-white',
                'transition-all duration-200',
                'hover:bg-gray-800 hover:border-gray-300 hover:shadow-sm',
                'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300',
                'touch-manipulation active:scale-[0.98]',
                'max-sm:py-4 max-sm:text-base',
              )}
              disabled={isLoading}
              aria-label={`Sign in with your enterprise email`}
              aria-describedby="auth-description"
            >
              <MailIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
              Sign in with Magic Link
            </button>
          )}
          {enableMagicLink && showMagicLinkInput && (
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <MailIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="email"
                placeholder="Enter your enterprise email"
                value={magicLinkEmail}
                onChange={(e) => setMagicLinkEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleMagicLinkLogin();
                  }
                }}
                className={cn(
                  'block w-full rounded-full border border-gray-300 bg-white py-3 pl-10 pr-24',
                  'text-gray-900 placeholder:text-gray-400',
                  'transition-colors duration-150',
                  'focus:border-black focus:outline-none focus:ring-1 focus:ring-black',
                )}
              />
              <button
                onClick={handleMagicLinkLogin}
                type="button"
                className={cn(
                  'absolute inset-y-1.5 right-1.5 flex items-center justify-center gap-1.5',
                  'rounded-full bg-black px-3 text-sm font-medium text-white',
                  'transition-all duration-150',
                  'hover:bg-Blue-Dark-Text active:scale-95',
                  'focus:outline-none focus:ring-2 focus:ring-True-Blue focus:ring-offset-1',
                )}
              >
                Request Code
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {/* Hidden description for screen readers */}
          <div id="auth-description" className="sr-only">
            Click to sign in using {providerConfig.displayName} authentication.
            You will be redirected to a secure sign-in page.
          </div>

          {/* Enhanced Loading States */}
          {flowState.state === 'initiating' && (
            <div className="py-4">
              <AuthLoadingStates.PreparingRedirect />
            </div>
          )}

          {flowState.state === 'redirecting' && (
            <div className="py-4">
              <EnhancedLoadingState
                message={`Redirecting to ${providerConfig.displayName}...`}
                submessage="Please wait while we redirect you to sign in"
                variant="spinner"
                size="md"
              />
            </div>
          )}

          {isLoading && (
            <div className="py-4">
              {AuthLoadingStates.Mobile('Authenticating...')}
            </div>
          )}

          {/* Enhanced Error State */}
          {displayError && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4"
              role="alert"
              aria-live="assertive"
            >
              <p className="text-red-700 text-sm mb-3" id="error-message">
                {displayError}
              </p>
              <button
                onClick={flowActions.reset}
                className={cn(
                  'bg-red-600 text-white px-4 py-3 rounded-md text-sm font-medium transition-colors',
                  'hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                  'touch-manipulation active:scale-95',
                  'max-sm:w-full max-sm:py-4', // Mobile responsive styling
                )}
                aria-describedby="error-message"
                aria-label="Try signing in again"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-6">
          <p className="text-xs text-gray-500 text-center">
            Powered by {providerConfig.footer}
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default CustomLoginPage;
